import React, { JSX, useState, useEffect } from "react";
import { useGetProviderGoal } from "data/data-access-hooks/providers/providers-queries";
import { t } from "utilities/localization/t";
import { ProviderRecord } from "models/view-models/providers/provider-record";
import { Card } from "components/card/card";
import {
    Paragraph,
    ParagraphSize,
    ParagraphStyle,
} from "components/typography/paragraph/paragraph";
import "./provider-goal-card.scss";
import { Select } from "../../form/inputs/select/select";
import { ProviderService } from "utilities/services/providers/provider-service";
import { clsx } from "clsx";
import { ProviderGoal } from "../../../models/interfaces/providers/provider-goal";
import { Spinner } from "../../spinner/spinner";
// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------

interface ProviderGoalCardProps {
    provider?: ProviderRecord;
}

// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Constants
// -------------------------------------------------------------------------------------------------

const CSS_CLASS_NAME = "provider-goal-card";

// #endregion Constants

// -------------------------------------------------------------------------------------------------
// #region Component
// -------------------------------------------------------------------------------------------------

const ProviderGoalCard: React.FC<ProviderGoalCardProps> = ({ provider }): JSX.Element => {
    const { list: getGoals } = ProviderService.useGetProviderGoal();
    const [goalYear, setGoalYear] = useState(new Date().getFullYear().toString());

    const { data: goalsData, isLoading: isLoadingGoals } = useGetProviderGoal(
        getGoals,
        provider?.id ?? 0
    ) as { data: Record<string, ProviderGoal>; isLoading: boolean };

    const balance = goalsData?.[goalYear]?.currentInvoiceTotal ?? 0,
        limit = goalsData?.[goalYear]?.goalAmount ?? 0;

    useEffect(() => {
        if (goalsData && Object.keys(goalsData).length) {
            const currentYearGoal = Object.values(goalsData).find(
                (goal) => goal?.year?.toString() === goalYear
            );
            if (currentYearGoal?.year) {
                setGoalYear(currentYearGoal?.year?.toString());
            }
        }
    }, [goalsData, goalYear]);

    const completionPercentage = +Math.min(100, (balance / limit) * 100).toFixed(0);

    return (
        <Card cssClassName={`${CSS_CLASS_NAME} flex flex-col gap-5 basis-2/5`}>
            <div className={`flex`}>
                <Paragraph
                    cssClassName={"basis-4/5"}
                    size={ParagraphSize.Large}
                    style={ParagraphStyle.Bold}>
                    {t("providerFee")}
                </Paragraph>
                {!isLoadingGoals &&
                    ((Object.keys(goalsData ?? {}).length ?? 0) > 1 ? (
                        <div className="basis-1/5 select-wrapper">
                            <Select
                                dataTestId={"provider-goal-card:year:select"}
                                id="goal-year"
                                onChange={(e) => setGoalYear(e.target.value)}
                                options={
                                    Object.values(goalsData ?? {})
                                        .map((goal) => ({
                                            text: goal.year?.toString() ?? "",
                                            value: goal.year?.toString() ?? "",
                                        }))
                                        .sort((a, b) => (b.value > a.value ? 1 : -1)) ?? []
                                }
                                value={goalYear}
                            />
                        </div>
                    ) : (
                        <Paragraph
                            cssClassName="basis-1/5"
                            size={ParagraphSize.Large}
                            style={ParagraphStyle.Bold}>
                            {goalYear}
                        </Paragraph>
                    ))}
            </div>
            {isLoadingGoals ? (
                <Spinner />
            ) : (
                <>
                    <div className={"flex flex-col gap-3"}>
                        <div className="flex flex-col gap-2 w-72">
                            <div>
                                <span className={"font-bold text-xl"}>${balance}</span> of ${limit}
                            </div>
                            <div
                                className="h-2 w-full rounded-full overflow-hidden"
                                style={{ backgroundColor: "#d1d5db" }}>
                                <div
                                    className={clsx(
                                        "h-full rounded-full bg-gradient-to-r transition-all duration-500 ease-out",
                                        completionPercentage === 100
                                            ? "from-accents-green-dark to-accents-green-base"
                                            : "from-accents-red-dark to-accents-red-base"
                                    )}
                                    style={{ width: `${completionPercentage}%` }}
                                />
                            </div>
                        </div>
                        <Paragraph style={ParagraphStyle.Light}>
                            {completionPercentage}% {t("complete")}
                        </Paragraph>
                    </div>
                    <div className={"italic mt-auto"}>
                        <span className="font-bold">
                            {t("goalDue")}{" "}
                            {new Date(goalsData?.[goalYear]?.deadline ?? "").toLocaleDateString(
                                undefined,
                                { month: "long", day: "numeric", timeZone: "UTC" }
                            )}
                        </span>
                        : {t("remainingBalanceWillBeInvoiced")}.
                    </div>
                </>
            )}
        </Card>
    );
};

// #endregion Component

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export { ProviderGoalCard };

// #endregion Exports
